import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Sidebar from 'components/Sidebar';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Container from 'components/Container';
import FolderAddAreasPopup from 'components/Folders/FolderAddAreasPopup';
import FolderAddDocumentPopup from 'components/Folders/FolderAddDocumentPopup';
import FolderAddOfficesPopup from 'components/Folders/FolderAddOfficesPopup';
import FolderAddTagPopup from 'components/Folders/FolderAddTagPopup';
import MobileHeader from 'components/MobileHeader';
import SearchOverlay from 'components/SearchOverlay';


import { deleteFolderArea, deleteFolderOffice, deleteFolderRule, deleteFolderTag, getFolder, updateFolder } from 'api/folders';
import Button from 'components/Button';
import FolderAddRulePopup from 'components/Folders/FolderAddRulePopup';
import TextInput from 'components/Form/TextInput';


export default function Create() {

    const [sidebar, setSidebar] = useState(false);
    const [search, setSearch] = useState(false);
    const { folderId } = useParams();

    const [addDocumentPopup, setAddDocumentPopup] = useState(false);
    const [addTagPopup, setAddTagPopup] = useState(false);
    const [addAreaPopup, setAddAreaPopup] = useState(false);
    const [addOfficePopup, setAddOfficePopup] = useState(false);
    const [addRulesPopup, setAddRulesPopup] = useState(false);

    const cache = useQueryClient();
    const navigate = useNavigate();

    const { data: folder } = useQuery({
        queryKey: ['folders', folderId],
        queryFn: async () => await getFolder({ id: folderId }),
        enabled: Boolean(folderId),
    });

    const { mutate } = useMutation({
        mutationKey: ['createFolder'],
        mutationFn: updateFolder,
        onSuccess: (data) => {
            console.log("Folder created successfully:", data);
            cache.invalidateQueries(['folders']);

            return navigate(`/settings/documents/${data._id}`);
        },
        onError: (error) => {
            console.error("Error creating Folder:", error);
        }
    });

    //#region Area funzioni di delete
    const { mutate: deleteArea } = useMutation({
        mutationKey: ['deleteFolderArea'],
        mutationFn: deleteFolderArea,
        onSuccess: () => {
            cache.invalidateQueries(['folders', folderId]);
        },
        onError: (error) => {
            console.error("Error removing Area from folder:", error);
        }
    });
    function deleteAreaHandler(id) {
        return deleteArea({
            id: folderId,
            areaId: id
        });
    }

    const { mutate: deleteTag } = useMutation({
        mutationKey: ['deleteFolderTag'],
        mutationFn: deleteFolderTag,
        onSuccess: () => {
            cache.invalidateQueries(['folders', folderId]);
        },
        onError: (error) => {
            console.error("Error removing tag from folder:", error);
        }
    });
    function deleteTagHandler(id) {
        return deleteTag({
            id: folderId,
            tagId: id
        });
    }

    const { mutate: deleteOffice } = useMutation({
        mutationKey: ['deleteFolderOffice'],
        mutationFn: deleteFolderOffice,
        onSuccess: () => {
            cache.invalidateQueries(['folders', folderId]);
        },
        onError: (error) => {
            console.error("Error removing office from folder:", error);
        }
    });
    function deleteOfficeHandler(id) {
        return deleteOffice({
            id: folderId,
            officeId: id
        });
    }

    const { mutate: deleteRules } = useMutation({
        mutationKey: ['deleteFolderRule'],
        mutationFn: deleteFolderRule,
        onSuccess: () => {
            cache.invalidateQueries(['folders', folderId]);
        },
        onError: (error) => {
            console.error("Error removing rules from folder:", error);
        }
    });
    function deleteRulesHandler(id) {
        return deleteRules({
            id: folderId,
            ruleId: id
        });
    }

    //#endregion

    function onSubmitHandler(formData) {
        const data = {
            name: formData.get('name'),
            description: formData.get('description'),
            baseUrl: formData.get('baseUrl'),
            apiKey: formData.get('apiKey'),
        }
        mutate({ id: folderId, data });
    }

    if (!folder) {
        return <></>;
    }

    return (
        <>
            <Sidebar
                visible={sidebar}
                setVisible={setSidebar}
            />

            <SearchOverlay
                visible={search}
                setVisible={setSearch}
            />

            <MobileHeader
                sidebar={sidebar}
                setSidebar={setSidebar}
                search={search}
                setSearch={setSearch}
            />

            <form action={onSubmitHandler}>
                <div className={`fixed top-0 w-full bottom-0 transition-all duration-300 ease-in-out lg:pl-[320px] overflow-y-auto ${sidebar ? 'left-[80vw]' : 'left-0'}`}>
                    <div className={`z-10 fixed top-0 w-[calc(100%-320px)] left-[320px] right-0 border-b border-light py-3 px-6 flex justify-between items-center backdrop-blur-sm`}>
                        <div className='flex gap-2 items-center'>
                            <p className='text-lg leading-none'>Cartella {folder?.name}</p>
                        </div>

                        <div>
                            <Button submit>Salva modifiche</Button>
                        </div>
                    </div>

                    <Container>
                        <div className='flex items-start gap-10 pt-30 pb-20'>
                            <div className='flex-1 scroll-auto'>
                                <div className='md:flex gap-5'>
                                    <TextInput
                                        label="Nome"
                                        name="name"
                                        placeholder="Nome"
                                        defaultValue={folder.name}
                                    />
                                    <TextInput
                                        label="Descrizione"
                                        name="description"
                                        placeholder="Descrizione"
                                        defaultValue={folder.description}
                                    />
                                </div>

                                <div className='pt-10'>
                                    <p className='text-sm block'>Regole chunk</p>

                                    <div className='pt-2'>
                                        {folder?.rules?.map(({ _id, ...rule }) => {
                                            return (
                                                <div key={_id} className='w-full border border-light rounded-md px-4 py-3 mb-3'>
                                                    <p className='text-sm'>{rule?.type}</p>
                                                    <p className='text-xs text-muted pt-1'>{rule?.value}</p>
                                                    <div className='hover:text-red-400 hover:underline cursor-pointer' onClick={() => deleteRulesHandler(_id)}>
                                                        Rimuovi
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        <div className='mt-4 border border-dashed border-greylight rounded-sm hover:bg-verylight cursor-pointer transition-colors duration-200'>
                                            <div className='w-full px-4 py-3 flex justify-center' onClick={() => setAddRulesPopup(true)}>
                                                <p className='text-sm text-muted'>+ Aggiungi Regola</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className='pt-10'>
                                    <p className='text-sm block'>Tag Collegati</p>

                                    <div className='pt-2'>
                                        {folder?.tags?.map(({ tag, _id }) => {
                                            return (
                                                <div key={tag._id} className='w-full border border-light rounded-md px-4 py-3 mb-3'>
                                                    <p className='text-sm'>{tag.name}</p>
                                                    <p className='text-xs text-muted pt-1'>ID: {tag._id}</p>
                                                    <div className='hover:text-red-400 hover:underline cursor-pointer' onClick={() => deleteTagHandler(_id)}>
                                                        Rimuovi
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        <div className='mt-4 border border-dashed border-greylight rounded-sm hover:bg-verylight cursor-pointer transition-colors duration-200'>
                                            <div className='w-full px-4 py-3 flex justify-center' onClick={() => setAddTagPopup(true)}>
                                                <p className='text-sm text-muted'>+ Aggiungi Tag</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='pt-10'>
                                    <p className='text-sm block'>Aree Collegate</p>

                                    <div className='pt-2'>
                                        {folder?.areas?.map(({ area, _id }) => {
                                            return (
                                                <div key={area._id} className='w-full border border-light rounded-md px-4 py-3 mb-3'>
                                                    <p className='text-sm'>{area.name}</p>
                                                    <p className='text-xs text-muted pt-1'>ID: {area._id}</p>
                                                    <div className='hover:text-red-400 hover:underline cursor-pointer' onClick={() => deleteAreaHandler(_id)}>
                                                        Rimuovi
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        <div className='mt-4 border border-dashed border-greylight rounded-sm hover:bg-verylight cursor-pointer transition-colors duration-200'>
                                            <div className='w-full px-4 py-3 flex justify-center' onClick={() => setAddAreaPopup(true)}>
                                                <p className='text-sm text-muted'>+ Aggiungi Area</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='pt-10'>
                                    <p className='text-sm block'>Uffici Collegati</p>

                                    <div className='pt-2'>
                                        {folder?.offices?.map(({ office, _id }) => {
                                            return (
                                                <div key={office._id} className='w-full border border-light rounded-md px-4 py-3 mb-3'>
                                                    <p className='text-sm'>{office.name}</p>
                                                    <p className='text-xs text-muted pt-1'>ID: {office._id}</p>
                                                    <div className='hover:text-red-400 hover:underline cursor-pointer' onClick={() => deleteOfficeHandler(_id)}>
                                                        Rimuovi
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        <div className='mt-4 border border-dashed border-greylight rounded-sm hover:bg-verylight cursor-pointer transition-colors duration-200'>
                                            <div className='w-full px-4 py-3 flex justify-center' onClick={() => setAddOfficePopup(true)}>
                                                <p className='text-sm text-muted'>+ Aggiungi Ufficio</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Container>
                </div >
            </form >

            <FolderAddRulePopup
                visible={addRulesPopup}
                setVisible={setAddRulesPopup}
                present={folder?.rules?.map(rule => rule._id)}
            />

            <FolderAddDocumentPopup
                visible={addDocumentPopup}
                setVisible={setAddDocumentPopup}
                present={folder?.documents?.map(document => document._id)}
            />

            <FolderAddTagPopup
                visible={addTagPopup}
                setVisible={setAddTagPopup}
                present={folder?.tags?.map(tag => tag._id)}
            />
            <FolderAddAreasPopup
                visible={addAreaPopup}
                setVisible={setAddAreaPopup}
                present={folder?.areas?.map(area => area._id)}
            />
            <FolderAddOfficesPopup
                visible={addOfficePopup}
                setVisible={setAddOfficePopup}
                present={folder?.offices?.map(office => office._id)}
            />
        </>
    )
}
